export const SeriesType = {
    BARS: 'bars',
    LINE: 'line',
} as const;

export type SeriesTypeValue = (typeof SeriesType)[keyof typeof SeriesType];

export const AnnotationType = {
    GrowthPercent: 'Growth %',
    Value: 'Value',
} as const;

export type AnnotationTypeValue = (typeof AnnotationType)[keyof typeof AnnotationType];

export const ChartStyle = {
    Line: 'line',
    Column: 'column',
} as const;

export type ChartStyleValue = (typeof ChartStyle)[keyof typeof ChartStyle];

export const AnnotationSize = {
    S: 11,
    M: 13,
    L: 15,
    XL: 17,
} as const;

export const ChartSign = {
    OFF: 0,
    POSITIVE: 1,
    NEGATIVE: -1,
} as const;

export type ChartSignValue = (typeof ChartSign)[keyof typeof ChartSign];

export const Resolution = {
    LOW: 1,
    MEDIUM: 2,
    HIGH: 3,
} as const;

export type ResolutionValue = (typeof Resolution)[keyof typeof Resolution];

export const ExportFormat = {
    PNG: 'png',
    JPEG: 'jpeg',
    WEBP: 'webp',
} as const;

export type ExportFormatValue = (typeof ExportFormat)[keyof typeof ExportFormat];

/**
 * Minimal shape of a flat descriptor as consumed by the chart module.
 * The full descriptor objects carry additional properties but the chart
 * only relies on these.
 */
export type ChartDescriptor = {
    id: string;
    label: string;
    type: string;
    viewScale?: number;
};

/**
 * Minimal shape of a report as consumed by the chart module.
 * Reports are keyed by descriptor id for dynamic field values.
 */
export type ChartReport = {
    year: number;
    period: string;
    [key: string]: unknown;
};

export type ConsolidatedMode = 'category' | 'standardized';

/** Per-field configuration — one entry per chartable descriptor */
export type FieldConfig = {
    descriptorType: string;
    annotationsEnabled: boolean;
    ratio: boolean;
    seriesType: SeriesTypeValue;
    label: string;
    chartSign: ChartSignValue;
    commonFieldId?: string;
    /** When true, the field gets its own dedicated right-side axis instead of
     *  sharing the type-based axis with other fields of the same type. */
    separateAxis: boolean;
};

/** Global chart display configuration driven by the toolbar controls */
export type ChartConfig = {
    showAnnotations: boolean;
    style: ChartStyleValue;
    showGridLines: boolean;
    annotationType: AnnotationTypeValue;
    matchSeriesColor: boolean;
    startFromZero: boolean;
    annotationSize: number;
};

/** Configuration for the download/export modal */
export type DownloadConfig = {
    title: string;
    width: number;
    height: number;
    backgroundColor: string;
    showHeadings: boolean;
    resolution: ResolutionValue;
    exportAs: ExportFormatValue;
    fileName: string;
};

/** One item in the custom legend below the chart */
export type LegendItem = {
    id: string;
    label: string;
    color: string;
    totalChange: number | null;
};

/** A single data point in an ECharts series — value + optional annotation label */
export type DataPoint = {
    value: number | null;
    annotation: string | null;
};

/**
 * Resolved axis presence flags and y-axis array indices.
 * numberAxisIndex / ratioAxisIndex / percentAxisIndex are -1 when that axis is not used.
 * separateAxisIndices maps composite series keys to their dedicated axis index;
 * fields not present here use the shared type-based axes above.
 */
export type AxisConfig = {
    hasNumber: boolean;
    hasRatio: boolean;
    hasPercent: boolean;
    numberAxisIndex: number;
    ratioAxisIndex: number;
    percentAxisIndex: number;
    separateAxisIndices: Record<string, number>;
};

/**
 * A single data source for the unified chart.
 * Whether it's 1 company or 1000, the logic is identical.
 */
export type ChartDataSource = {
    id: number;
    label: string;
    companyId: number;
    companyName: string;
    companyLegendLabel?: string;
    schemaId: number;
    schemaName: string;
    schemaLegendLabel?: string;
    descriptors: ChartDescriptor[];
    reports: ChartReport[];
};

/** Per-source state stored in the chart store */
export type DataSourceState = {
    id: number;
    label: string;
    companyId: number;
    companyName: string;
    companyLegendLabel?: string;
    schemaId: number;
    schemaName: string;
    schemaLegendLabel?: string;
    reports: ChartReport[];
    fieldsCfg: Record<string, FieldConfig>;
    selectedFieldIds: string[];
};
